export type PhotoTransform = {scale: number; x: number; y: number};
export type PhotoPoint = {x: number; y: number};
export type PhotoSize = {width: number; height: number};
export const initialPhotoTransform: PhotoTransform = {scale: 1, x: 0, y: 0};
export const MAX_PHOTO_ZOOM = 4;

// RN supplies source dimensions; RN Web supplies an HTML image load event.
// Read only the already-loaded image, without a second media request.
export function loadedPhotoSize(event: unknown, renderedImage?: {naturalWidth: number; naturalHeight: number} | null): PhotoSize | null {
  const native = (event as {nativeEvent?: {source?: PhotoSize; target?: {naturalWidth?: number; naturalHeight?: number}}} | null)?.nativeEvent;
  const width = native?.source?.width ?? native?.target?.naturalWidth ?? renderedImage?.naturalWidth;
  const height = native?.source?.height ?? native?.target?.naturalHeight ?? renderedImage?.naturalHeight;
  return typeof width === 'number' && typeof height === 'number'
    && Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
    ? {width, height} : null;
}

export function constrainPhoto(transform: PhotoTransform, viewport: PhotoSize, image: PhotoSize): PhotoTransform {
  if (![transform.scale, transform.x, transform.y, viewport.width, viewport.height, image.width, image.height].every(Number.isFinite)
      || Math.min(viewport.width, viewport.height, image.width, image.height) <= 0) return initialPhotoTransform;
  const scale = Math.min(MAX_PHOTO_ZOOM, Math.max(1, transform.scale));
  const fit = Math.min(viewport.width / image.width, viewport.height / image.height);
  const limitX = Math.max(0, (image.width * fit * scale - viewport.width) / 2);
  const limitY = Math.max(0, (image.height * fit * scale - viewport.height) / 2);
  return {scale, x: Math.max(-limitX, Math.min(limitX, transform.x)), y: Math.max(-limitY, Math.min(limitY, transform.y))};
}

export function movePhoto(current: PhotoTransform, previous: PhotoPoint[], next: PhotoPoint[], viewport: PhotoSize, image: PhotoSize): PhotoTransform {
  if (!previous.length || previous.length !== next.length || next.length > 2) return current;
  const center = (points: PhotoPoint[]) => ({x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length});
  const a = center(previous); const b = center(next);
  const distance = (points: PhotoPoint[]) => Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
  const oldDistance = previous.length === 2 ? distance(previous) : 0;
  const scale = oldDistance > 0 ? Math.min(MAX_PHOTO_ZOOM, Math.max(1, current.scale * distance(next) / oldDistance)) : current.scale;
  const ratio = scale / current.scale;
  return constrainPhoto({scale,
    x: b.x - viewport.width / 2 - (a.x - viewport.width / 2 - current.x) * ratio,
    y: b.y - viewport.height / 2 - (a.y - viewport.height / 2 - current.y) * ratio}, viewport, image);
}
